import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la información',
    body: 'Tu información se utiliza exclusivamente para: entregar el producto digital adquirido, enviarte actualizaciones relacionadas con tu compra, y brindarte soporte si lo necesitas. No vendemos ni compartimos tu información con terceros.'
  }, {
    heading: 'Cookies',
    body: 'Utilizamos cookies esenciales para el funcionamiento del sitio y cookies analíticas para entender cómo los visitantes interactúan con nuestra página. Puedes desactivar las cookies en la configuración de tu navegador.'
  }, {
    heading: 'Tus derechos',
    body: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos directamente.'
  }, {
    heading: 'Cambios a esta política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad. Cualquier cambio será publicado en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto digital',
    body: 'Mamá 10-Minutos: Cuerpo Firme Sin Gym es un producto digital en formato PDF. Al completar tu compra, recibirás acceso inmediato para descargar el material.'
  }, {
    heading: 'Uso personal',
    body: 'El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.'
  }, {
    heading: 'Política de reembolso',
    body: 'Debido a la naturaleza digital del producto, no se ofrecen reembolsos una vez realizada la descarga. Si tienes problemas técnicos para acceder al material, contáctanos y te ayudaremos a resolverlo.'
  }, {
    heading: 'Descargo de responsabilidad',
    body: 'Este programa es informativo y no sustituye el consejo médico profesional. Consulta con tu médico antes de iniciar cualquier programa de ejercicio, especialmente si estás en período postparto o tienes condiciones médicas preexistentes.'
  }, {
    heading: 'Resultados',
    body: 'Los resultados pueden variar de persona a persona. No garantizamos resultados específicos, ya que estos dependen de factores individuales como la constancia, alimentación y condición física inicial.'
  }]
};
const overlayVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.25
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};
const modalVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.97
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.35,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    y: 20,
    scale: 0.97,
    transition: {
      duration: 0.2
    }
  }
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const openModal = (type: ModalType) => {
    setActiveModal(type);
    document.body.style.overflow = 'hidden';
  };
  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = '';
  };
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="w-full px-5 py-10" style={{
      backgroundColor: 'var(--color-slate)'
    }} data-id="element-19">
        <div className="max-w-[720px] mx-auto flex flex-col items-center gap-4" data-id="element-20">
          <div className="flex items-center gap-6" data-id="element-21">
            <button onClick={() => openModal('privacy')} className="text-sm transition-opacity duration-200 hover:opacity-100 cursor-pointer" style={{
            color: 'var(--color-cream)',
            opacity: 0.5
          }} data-id="element-22">
              Política de Privacidad
            </button>
            <span className="text-sm" style={{
            color: 'var(--color-cream)',
            opacity: 0.2
          }} data-id="element-23">
              ·
            </span>
            <button onClick={() => openModal('terms')} className="text-sm transition-opacity duration-200 hover:opacity-100 cursor-pointer" style={{
            color: 'var(--color-cream)',
            opacity: 0.5
          }} data-id="element-24">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-xs" style={{
          color: 'var(--color-cream)',
          opacity: 0.3
        }} data-id="element-25">
            © {new Date().getFullYear()} Mamá 10-Minutos. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-26">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" variants={overlayVariants} initial="hidden" animate="visible" exit="exit" data-id="element-27">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} data-id="element-28" />

            {/* Modal content */}
            <motion.div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl p-7 md:p-9 shadow-2xl" style={{
          backgroundColor: 'var(--color-cream)'
        }} variants={modalVariants} initial="hidden" animate="visible" exit="exit" data-id="element-29">
              {/* Close button */}
              <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 cursor-pointer" style={{
            backgroundColor: 'var(--color-blush)'
          }} aria-label="Cerrar" data-id="element-30">
                <XIcon className="w-4 h-4" style={{
              color: 'var(--color-slate)'
            }} data-id="element-31" />
              </button>

              {/* Title */}
              <h3 className="text-2xl md:text-3xl mb-8 pr-8" style={{
            color: 'var(--color-slate)',
            fontFamily: "'DM Serif Display', serif"
          }} data-id="element-32">
                {content.title}
              </h3>

              {/* Sections */}
              <div className="space-y-6" data-id="element-33">
                {content.sections.map((section, index) => <div key={index} data-id="element-34">
                    <h4 className="text-base font-semibold mb-2" style={{
                color: 'var(--color-slate)'
              }} data-id="element-35">
                      {section.heading}
                    </h4>
                    <p className="text-sm leading-relaxed" style={{
                color: 'var(--color-slate)',
                opacity: 0.75
              }} data-id="element-36">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              {/* Last updated */}
              <div className="mt-8 pt-5" style={{
            borderTop: '1px solid var(--color-blush)'
          }} data-id="element-37">
                <p className="text-xs" style={{
              color: 'var(--color-slate)',
              opacity: 0.4
            }} data-id="element-38">
                  Última actualización: febrero 2026
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}